import type { RecipeProposal, RecipeProposalLine } from './recipeProposal';
import { normalizeIngredientName } from './recipeProposal';
import type { Unit } from './units';

/**
 * Preparación o plato (revisión con Alex del 12 oct, el caso croquetas).
 *
 * «Hago una masa de croquetas para unas 400… Las vendo en porciones de 8, con
 * alioli, a 9,90» son DOS cosas: una preparación que se hace en cantidad (la
 * masa, rinde 400 piezas, sin precio) y un plato que se vende (8 piezas de la
 * masa + alioli, a 9,90). Nadie tiene que saber qué es una preparación:
 * Alexia las separa, las enlaza y se guardan juntas.
 *
 * Todo es puro: lo usan la API (después del modelo, como red por si el
 * modelo devuelve una sola receta) y la demo (que simula el dictado).
 */

/** Desde dónde se crea: «Nuevo plato» (Tu carta) o «Nueva preparación». */
export type ContextoAlta = 'plato' | 'preparacion';

const PREP_SENALES: RegExp[] = [
  /\bpara un[oa]s? \d[\d.,]*(?:\s+(?:piezas|unidades|porciones|kg|kilos|litros|l)\b)?/i,
  /\bhago (?:una?|el|la|mi) (?:masa|salsa|caldo|adobo|fondo|base|sofrito|mezcla|aderezo|marinada|relleno|crema|pasta)\b[^,.;:]{0,30}/i,
  /\brinde (?:un[oa]s? )?\d[\d.,]*\s*[a-záéíóú]*/i,
  /\bsalen (?:un[oa]s? )?\d[\d.,]*\s*(?:kg|kilos?|litros?|l)\b/i,
];

const PLATO_SENALES: RegExp[] = [
  /\b(?:lo|la|los|las) vendo (?:a|en|por)\b[^,.;:]*/i,
  /\bla porci[oó]n lleva\b[^,.;:]*/i,
  /\bse vende a\b[^,.;:]*/i,
];

const PORCION: RegExp[] = [
  /\bporci[oó]n(?:es)? de (\d+)/i,
  /\b(\d+) por porci[oó]n/i,
  /\bla porci[oó]n lleva (\d+)/i,
];

const primera = (texto: string, patrones: RegExp[]): RegExpExecArray | undefined => {
  let mejor: RegExpExecArray | undefined;
  for (const patron of patrones) {
    const m = patron.exec(texto);
    if (m && (!mejor || m.index < mejor.index)) mejor = m;
  }
  return mejor;
};

const recorta = (frase: string): string => {
  const limpia = frase.trim().replace(/[\s,;:]+$/, '');
  return limpia.length > 48 ? `${limpia.slice(0, 45).trimEnd()}…` : limpia;
};

/** Lo que dijo y que delata qué es: «para unas 400», «las vendo en porciones de 8». */
export function senalesTipo(texto: string): { preparacion?: string; plato?: string; indicePlato?: number } {
  // En la preparación manda la más clara («para unas 400»), no la primera.
  const prep = PREP_SENALES.map((p) => p.exec(texto)).find((m): m is RegExpExecArray => Boolean(m));
  const plato = primera(texto, PLATO_SENALES);
  return {
    ...(prep ? { preparacion: recorta(prep[0]) } : {}),
    ...(plato ? { plato: recorta(plato[0]), indicePlato: plato.index } : {}),
  };
}

/** «Las vendo en porciones de 8» → 8. */
export function porcionDeTexto(texto: string): number | undefined {
  const m = primera(texto, PORCION);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** «para unas 400» → 400 piezas; «para unos 3 kilos» → 3 kg. */
function rindeDeTexto(texto: string): { qty: number; unit: Unit } | undefined {
  const m = /\bpara un[oa]s? (\d[\d.]*(?:,\d+)?)\s*(kg|kilos?|litros?|l)?\b/i.exec(texto);
  if (!m) return undefined;
  const qty = Number(m[1].replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
  if (!(qty > 0)) return undefined;
  const u = (m[2] ?? '').toLowerCase();
  return { qty, unit: u.startsWith('k') ? 'kg' : u.startsWith('l') ? 'l' : 'pz' };
}

/** «… a 9,90» (lo último que se dice después de «las vendo»). */
function precioDeTexto(texto: string, desde: number): number | undefined {
  const tramo = texto.slice(desde);
  const patron = /\ba \$?\s?(\d+(?:[.,]\d{1,2})?)(?!\s*(?:g\b|gr|gramos|kg|kilos|ml|l\b|litros|piezas|unidades|porciones))/gi;
  let ultimo: RegExpExecArray | null = null;
  for (let m = patron.exec(tramo); m; m = patron.exec(tramo)) ultimo = m;
  if (!ultimo) return undefined;
  const n = Number(ultimo[1].replace(',', '.'));
  return n > 0 ? n : undefined;
}

const PALABRA_PREP = /^(masa|salsa|caldo|base|fondo|adobo|sofrito|mezcla|relleno|aderezo|marinada|crema|pasta)\b/i;

/**
 * El nombre del plato que sale de una preparación: «Masa de croquetas» y 8
 * piezas → «Croquetas (8 piezas)».
 */
export function platoDesdePreparacion(nombrePreparacion: string, cantidad: number, unidad: Unit = 'pz'): string {
  const base = nombrePreparacion.trim().replace(/^(masa|salsa|caldo|base|fondo|adobo|sofrito|mezcla|relleno|aderezo|marinada|crema|pasta)\s+(?:de|para)\s+/i, '').trim() || nombrePreparacion.trim();
  const nombre = base.charAt(0).toUpperCase() + base.slice(1);
  const cuanto = unidad === 'pz' ? `${cantidad} ${cantidad === 1 ? 'pieza' : 'piezas'}` : `${cantidad} ${unidad === 'l' ? 'L' : unidad}`;
  return `${nombre} (${cuanto})`;
}

const mismoNombre = (linea: string, receta: string): boolean => {
  const x = normalizeIngredientName(linea);
  const y = normalizeIngredientName(receta);
  if (!x || !y) return false;
  if (x === y) return true;
  // «la masa» → «Masa de croquetas»; «croquetas» → «Masa de croquetas». Nada
  // más laxo: «Arroz» no es «Arroz blanco».
  if (PALABRA_PREP.test(x) && !x.includes(' ') && y.startsWith(`${x} `)) return true;
  return PALABRA_PREP.test(y) && y.split(' ').slice(1).join(' ') === x;
};

/** Una sola receta que en realidad son dos: la masa y la porción que se vende. */
function separar(p: RecipeProposal, texto: string): RecipeProposal[] | undefined {
  const senales = senalesTipo(texto);
  const porcion = porcionDeTexto(texto);
  const pareceVenta = Boolean(senales.plato || p.sellingPriceGross);
  const pareceTanda = Boolean(senales.preparacion || p.isSubrecipe);
  if (!pareceVenta || !pareceTanda || !porcion) return undefined;
  const desde = senales.indicePlato ?? texto.search(PORCION[0]);
  const rinde = rindeDeTexto(texto);

  const enTexto = (linea: RecipeProposalLine) => texto.toLowerCase().indexOf(linea.rawText.toLowerCase());
  const delPlato = (linea: RecipeProposalLine) => desde >= 0 && enTexto(linea) >= desde;

  const hecha = /\bhago (?:una?|el|la|mi) (masa|salsa|caldo|adobo|fondo|base|sofrito|mezcla|relleno)\s+(?:de|para)\s+([^,.;:]+?)(?:\s+para\b|[,.;:]|$)/i.exec(texto);
  const nombrePrep = PALABRA_PREP.test(p.name.trim())
    ? p.name.trim()
    : hecha ? `${hecha[1].charAt(0).toUpperCase()}${hecha[1].slice(1).toLowerCase()} de ${hecha[2].trim()}`
      : `Masa de ${p.name.trim().toLowerCase()}`;

  const preparacion: RecipeProposal = {
    name: nombrePrep,
    isSubrecipe: true,
    yieldQty: rinde?.qty ?? p.yieldQty ?? p.servings,
    yieldUnit: rinde?.unit ?? p.yieldUnit ?? 'pz',
    lines: p.lines.filter((l) => !delPlato(l)),
    ...(p.steps?.length ? { steps: p.steps } : {}),
    ...(p.notes?.length ? { notes: p.notes } : {}),
    ...(senales.preparacion ? { porque: senales.preparacion } : {}),
  };
  const porcionTexto = primera(texto, PORCION);
  const plato: RecipeProposal = {
    name: platoDesdePreparacion(nombrePrep, porcion, preparacion.yieldUnit === 'pz' ? 'pz' : preparacion.yieldUnit),
    servings: 1,
    ...(p.group ? { group: p.group } : {}),
    sellingPriceGross: p.sellingPriceGross ?? (desde >= 0 ? precioDeTexto(texto, desde) : undefined),
    lines: [
      { rawText: porcionTexto ? porcionTexto[0] : `porciones de ${porcion}`, ingredientName: nombrePrep, qty: porcion, unit: preparacion.yieldUnit === 'pz' ? 'pz' : 'g', isSubrecipe: true, fromProposal: 0, estimated: {} },
      ...p.lines.filter(delPlato),
    ],
    ...(senales.plato ? { porque: senales.plato } : {}),
  };
  if (plato.sellingPriceGross == null) delete plato.sellingPriceGross;
  return [preparacion, plato];
}

/**
 * Ordena lo que devolvió el modelo (o la demo) para el paso 2:
 *  1. Una receta que mezcla la tanda y la porción se separa en dos.
 *  2. Lo que un plato del mismo dictado usa es preparación y va delante; la
 *     línea del plato queda enlazada (`fromProposal`).
 *  3. Desde Preparaciones, la duda se resuelve hacia preparación salvo que
 *     diga «lo vendo a…».
 *  4. `porque`: la frase que lo delata, para «Lo marqué porque dijiste…».
 */
export function ordenarPropuestas(
  propuestas: RecipeProposal[],
  opciones: { texto?: string; contexto?: ContextoAlta } = {},
): RecipeProposal[] {
  const texto = opciones.texto ?? '';
  let lista = propuestas.map((p) => ({ ...p, lines: p.lines.map((l) => ({ ...l })) }));
  if (lista.length === 1 && texto) lista = separar(lista[0], texto) ?? lista;

  // Enlazar: la línea de un plato que se llama como otra receta del dictado.
  const usadas = new Set<number>();
  const enlaces = new Map<RecipeProposalLine, RecipeProposal>();
  lista.forEach((p, i) => p.lines.forEach((l) => {
    const j = l.fromProposal != null && l.fromProposal !== i && lista[l.fromProposal]
      ? l.fromProposal
      : lista.findIndex((q, k) => k !== i && mismoNombre(l.ingredientName, q.name));
    if (j >= 0) { usadas.add(j); enlaces.set(l, lista[j]); }
  }));
  for (const j of usadas) {
    const prep = lista[j];
    prep.isSubrecipe = true;
    // El precio es del plato que la usa, no de la tanda.
    if (prep.sellingPriceGross != null) {
      const plato = lista.find((p) => p.lines.some((l) => enlaces.get(l) === prep));
      if (plato && plato.sellingPriceGross == null) plato.sellingPriceGross = prep.sellingPriceGross;
      delete prep.sellingPriceGross;
    }
    if (!prep.yieldQty && prep.servings) { prep.yieldQty = prep.servings; prep.yieldUnit = prep.yieldUnit ?? 'pz'; }
  }
  // Lo que se usa va delante (se guarda primero).
  const orden = [...lista.filter((_, i) => usadas.has(i)), ...lista.filter((_, i) => !usadas.has(i))];
  for (const p of orden) {
    for (const l of p.lines) {
      const destino = enlaces.get(l);
      if (destino) { l.isSubrecipe = true; l.fromProposal = orden.indexOf(destino); }
      else delete l.fromProposal;
    }
  }

  const senales = texto ? senalesTipo(texto) : {};
  return orden.map((p) => {
    const out = { ...p };
    const sinVenta = !out.sellingPriceGross && !senales.plato;
    // Solo dictó la masa («hago una masa… para unas 400»), o entra desde Preparaciones con duda.
    if (orden.length === 1 && sinVenta && (opciones.contexto === 'preparacion' || senales.preparacion)) out.isSubrecipe = true;
    if (orden.length === 1 && out.isSubrecipe) {
      const rinde = texto ? rindeDeTexto(texto) : undefined;
      // 400 piezas no son 50 porciones: manda lo que dijo que rinde.
      if (rinde && (!out.yieldQty || out.yieldQty === out.servings)) { out.yieldQty = rinde.qty; out.yieldUnit = rinde.unit; }
      else if (!out.yieldQty && out.servings) { out.yieldQty = out.servings; out.yieldUnit = out.yieldUnit ?? 'pz'; }
    }
    if (!out.porque) {
      const frase = out.isSubrecipe ? senales.preparacion : senales.plato;
      if (frase) out.porque = frase;
    }
    return out;
  });
}

/* ------------------------------------------------------------------ */
/* Costo incompleto o aproximado (punto 2 de la revisión del 12 oct)   */
/* ------------------------------------------------------------------ */

export interface Faltante {
  nombre: string;
  /** Lo que pesa en la receta (g o ml); sin dato si va en piezas. */
  gramos?: number;
  /** Lo que costaría con un precio de referencia, si lo hay. */
  costoEstimado?: number;
  /** Sin cantidad («un poco de nuez moscada»): poca cosa por definición. */
  sinCantidad?: boolean;
}

/** Más de esto del costo (o del peso) y ya no se puede decir cuánto te deja. */
export const UMBRAL_FALTANTE_IMPORTANTE = 0.1;

/**
 * Qué falta que importa y qué es poca cosa. Importa si, con un precio de
 * referencia, sería más del 10 % del costo; sin referencia, si pesa más del
 * 10 % de la receta; y en piezas sin referencia, siempre (mejor «incompleto»
 * que un «te deja» falso). Lo que no tiene cantidad («un poco») es poca cosa.
 */
export function clasificarFaltantes(
  faltantes: Faltante[],
  { costoConocido, gramosTotales }: { costoConocido: number; gramosTotales: number },
): { importantes: string[]; menores: string[] } {
  const estimados = faltantes.reduce((s, f) => s + (f.costoEstimado ?? 0), 0);
  const base = costoConocido + estimados;
  const importantes: string[] = [];
  const menores: string[] = [];
  for (const f of faltantes) {
    const importa = f.sinCantidad ? false
      : f.costoEstimado != null ? base > 0 && f.costoEstimado / base > UMBRAL_FALTANTE_IMPORTANTE
      : f.gramos != null ? gramosTotales > 0 && f.gramos / gramosTotales > UMBRAL_FALTANTE_IMPORTANTE
      : true;
    (importa ? importantes : menores).push(f.nombre);
  }
  return { importantes, menores };
}

const FEMENINOS = new Set(['leche', 'sal', 'carne', 'miel', 'nuez', 'col', 'coliflor', 'flor', 'piel', 'crema', 'nata', 'mantequilla', 'harina', 'levadura', 'ralladura', 'clara', 'yema', 'masa', 'salsa', 'pimienta', 'canela', 'vainilla', 'mostaza', 'mayonesa', 'mermelada', 'gelatina', 'cebolla', 'papa', 'patata', 'zanahoria', 'tortilla', 'base', 'fruta', 'verdura', 'agua']);
const MASCULINOS_EN_A = new Set(['dia', 'tomate', 'aguacate', 'chile', 'ajo', 'aceite', 'azucar', 'arroz', 'queso', 'jamon', 'pan', 'pollo', 'huevo', 'perejil', 'cilantro', 'caldo', 'alioli', 'aioli']);

/** «la leche», «el jamón», «las papas». Heurística: si no sabe, «el». */
export function conArticulo(nombre: string): string {
  const limpio = nombre.trim().toLowerCase();
  const palabra = normalizeIngredientName(limpio).split(' ')[0] ?? '';
  const crudo = limpio.normalize('NFD').replace(/[̀-ͯ]/g, '').split(/\s+/)[0] ?? '';
  const pluralFem = /as$/.test(crudo);
  const pluralMasc = /os$|es$/.test(crudo) && !FEMENINOS.has(palabra);
  if (palabra === 'agua') return `el ${limpio}`;
  if (pluralFem) return `las ${limpio}`;
  if (pluralMasc && crudo !== palabra) return `los ${limpio}`;
  const fem = !MASCULINOS_EN_A.has(palabra) && (FEMENINOS.has(palabra) || /(a|ion|dad|tad|umbre)$/.test(palabra));
  return `${fem ? 'la' : 'el'} ${limpio}`;
}

/** «la leche y el jamón»; «a, b y c». */
export function listaConArticulos(nombres: string[]): string {
  const con = nombres.map(conArticulo);
  return con.length <= 1 ? (con[0] ?? '') : `${con.slice(0, -1).join(', ')} y ${con[con.length - 1]}`;
}

/* ------------------------------------------------------------------ */
/* Sugerencias de una vez (punto 7): frito sin aceite, con pan sin pan */
/* ------------------------------------------------------------------ */

export type Sugerencia = 'aceite' | 'pan';

const FRITO = /\b(fr[ií](?:o|es|en|e|to|ta|tos|tas)|fre[ií]r|freidora|empaniz\w*|rebozad\w*|croquetas?|tempura|churros?|nuggets?|milanesas?|papas fritas|patatas fritas)\b/i;
const CON_PAN = /\b(hamburguesas?|s[aá]ndwich(?:es)?|sandwich(?:es)?|bocadillos?|tortas?|tostas?|montaditos?|hot ?dogs?|perros? calientes?|lleva pan|con pan)\b/i;
const ES_ACEITE = /\baceite\b/i;
const ES_PAN = /\b(pan|panes|bollos?|baguette|telera|bolillo|chapata|brioche|pan de)\b/i;

/**
 * Lo típico que no dijo: si se fríe y no hay aceite, o lleva pan y no hay pan.
 * Nunca se agrega solo: el usuario elige «Agregar» o «No hace falta».
 */
export function sugerenciasPara(input: { texto?: string; nombre: string; lineas: string[] }): Sugerencia[] {
  const contexto = `${input.nombre} ${input.texto ?? ''}`;
  const out: Sugerencia[] = [];
  if (FRITO.test(contexto) && !input.lineas.some((l) => ES_ACEITE.test(l))) out.push('aceite');
  if (CON_PAN.test(contexto) && !input.lineas.some((l) => ES_PAN.test(l) && !/rallado|molido/i.test(l))) out.push('pan');
  return out;
}
